import { useEffect, useState } from 'react'
import { useAuth } from '../auth/useAuth.js'
import { useFlawDeskMe, useMyTickets, useTasksForMail } from '../api/hooks.js'
import CreateTicketForm from './CreateTicketForm.jsx'

/**
 * Task Pane — Hauptkomponente.
 */
export function Taskpane({ officeInfo }) {
  const auth = useAuth()
  const me = useFlawDeskMe(auth)
  const myTickets = useMyTickets(auth, { limit: 10 })
  const [subject, setSubject] = useState(() => readSubject())
  const [from, setFrom] = useState(() => readFrom())
  const [conversationId, setConversationId] = useState(() => readConversationId())
  const [internetMessageId, setInternetMessageId] = useState(() => readInternetMessageId())
  const [creating, setCreating] = useState(false)
  const [createdToast, setCreatedToast] = useState(null)
  // #1088: Tickets die mit dieser Mail oder dem Thread verknuepft sind
  const tasksForMail = useTasksForMail(auth, { internetMessageId, conversationId })

  useEffect(() => {
    if (!isInOutlook()) return

    const refresh = () => {
      setSubject(readSubject())
      setFrom(readFrom())
      setConversationId(readConversationId())
      setInternetMessageId(readInternetMessageId())
      // Wenn die Mail wechselt, soll der Erfolgs-Toast verschwinden — der
      // gehoerte zur vorherigen Mail.
      setCreatedToast(null)
      // Form ebenfalls schliessen, damit der User die neue Mail sieht.
      setCreating(false)
    }

    Office.context.mailbox.addHandlerAsync(
      Office.EventType.ItemChanged,
      refresh,
    )

    return () => {
      Office.context.mailbox.removeHandlerAsync(
        Office.EventType.ItemChanged,
        refresh,
      )
    }
  }, [])

  const inOutlook = isInOutlook()

  return (
    <div className="fd-shell">
      <header className="fd-header">
        {creating && (
          <button
            className="fd-header__back"
            onClick={() => setCreating(false)}
            aria-label="Zurueck"
            title="Zurueck"
          >
            ←
          </button>
        )}
        <div className="fd-header__logo">F</div>
        <div className="fd-header__title">
          {creating ? 'Neues Ticket' : 'FlawDesk for Outlook'}
        </div>
      </header>

      <main className="fd-body">
        {/* ── Create-Ticket-Page ────────────────────────────────── */}
        {creating && (
          <CreateTicketForm
            auth={auth}
            mailContext={{ subject, from, conversationId, internetMessageId }}
            onClose={() => setCreating(false)}
            onCreated={(ticket) => {
              setCreatedToast(ticket)
            }}
          />
        )}

        {/* ── Main-Page ────────────────────────────────────────── */}
        {!creating && (
          <>
            {/* AuthCard NUR zeigen wenn nicht ready — angemeldet ist
                Grundvoraussetzung, kein Status den der User staendig
                sieht. Bei Fehler oder Loading wirds wichtig. */}
            {auth.status !== 'ready' && <AuthCard auth={auth} />}

            {auth.status === 'ready' && (
              <>
                <FlawDeskUserCard me={me} />
                <MyTicketsCard tickets={myTickets} />
              </>
            )}

            {inOutlook && (
              <>
                <div className="fd-section-label">Aktuelle Mail</div>

                <div className="fd-card">
                  <div className="fd-card__label">Betreff</div>
                  <div
                    className={
                      subject
                        ? 'fd-card__value'
                        : 'fd-card__value fd-card__value--muted'
                    }
                  >
                    {subject || '(kein Betreff)'}
                  </div>
                </div>

                <div className="fd-card">
                  <div className="fd-card__label">Von</div>
                  <div
                    className={
                      from
                        ? 'fd-card__value'
                        : 'fd-card__value fd-card__value--muted'
                    }
                  >
                    {from || '(unbekannt)'}
                  </div>
                </div>

                {/* #1088: Tickets fuer diese Mail/Thread persistent anzeigen.
                    Bei Reload oder erneutem Oeffnen der Mail wird die Liste
                    aus der DB geholt. Wenn der User in dieser Session gerade
                    ein Ticket angelegt hat (createdToast gesetzt) und der
                    api/tasks/by-mail-Hook das noch nicht reflektiert, mergen
                    wir den frischen Eintrag rein — beim naechsten Mail-Wechsel
                    fragt der Hook frisch ab und sieht es selbst. */}
                <RelatedTasksCard
                  tasksForMail={tasksForMail}
                  freshTicket={createdToast}
                />

                {auth.status === 'ready' && (
                  <div className="fd-mail-actions">
                    <button
                      className="fd-btn fd-btn--primary fd-btn--block"
                      onClick={() => setCreating(true)}
                    >
                      Ticket aus dieser Mail erstellen
                    </button>
                  </div>
                )}
              </>
            )}

            {!inOutlook && (
              <div className="fd-card">
                <div className="fd-card__label">Hinweis</div>
                <div className="fd-card__value fd-card__value--muted">
                  Das Add-in läuft außerhalb von Outlook. Lade es als Sideload
                  in Outlook Web oder Desktop, um Mail-Daten zu sehen.
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <footer className="fd-footer">
        v{__APP_VERSION__} · {officeInfo?.host ?? 'standalone'}
      </footer>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Auth-UI
// ─────────────────────────────────────────────────────────────────────

function AuthCard({ auth }) {
  if (auth.status === 'idle' || auth.status === 'loading') {
    return (
      <div className="fd-card">
        <div className="fd-card__label">Anmeldung</div>
        <div className="fd-card__value fd-card__value--muted">
          Verbinde mit Microsoft 365…
        </div>
      </div>
    )
  }

  if (auth.status === 'error') {
    return (
      <div className="fd-card">
        <div className="fd-card__label">Anmeldung fehlgeschlagen</div>
        <div className="fd-card__value">{auth.message}</div>
        {auth.code && (
          <div className="fd-card__value fd-card__value--muted">
            Code: {auth.code}
          </div>
        )}
      </div>
    )
  }

  // status === 'ready'
  const acc = auth.account
  return (
    <div className="fd-card">
      <div className="fd-card__label">Angemeldet als</div>
      <div className="fd-card__value">
        {acc?.name || acc?.username || '(unbekannt)'}
      </div>
      {acc?.username && acc?.name && (
        <div className="fd-card__value fd-card__value--muted">
          {acc.username}
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// FlawDesk-API-Cards
// ─────────────────────────────────────────────────────────────────────

function FlawDeskUserCard({ me }) {
  if (me.status === 'idle' || me.status === 'loading') {
    return (
      <div className="fd-card">
        <div className="fd-card__label">FlawDesk-Profil</div>
        <div className="fd-card__value fd-card__value--muted">
          Lade Profil…
        </div>
      </div>
    )
  }

  if (me.status === 'error') {
    return (
      <div className="fd-card">
        <div className="fd-card__label">FlawDesk-Profil</div>
        <div className="fd-card__value">
          {translateApiError(me.error)}
        </div>
        {me.error?.code && (
          <div className="fd-card__value fd-card__value--muted">
            Code: {me.error.code}
          </div>
        )}
      </div>
    )
  }

  // status === 'ready'
  const u = me.user
  return (
    <div className="fd-card">
      <div className="fd-card__label">FlawDesk-Profil</div>
      <div className="fd-card__value">{u?.name || '(unbekannt)'}</div>
      <div className="fd-card__value fd-card__value--muted">
        {u?.email}
      </div>
    </div>
  )
}

function MyTicketsCard({ tickets }) {
  if (tickets.status === 'idle' || tickets.status === 'loading') {
    return (
      <div className="fd-card">
        <div className="fd-card__label">Meine offenen Tickets</div>
        <div className="fd-card__value fd-card__value--muted">Lade…</div>
      </div>
    )
  }

  if (tickets.status === 'error') {
    return (
      <div className="fd-card">
        <div className="fd-card__label">Meine offenen Tickets</div>
        <div className="fd-card__value">
          {translateApiError(tickets.error)}
        </div>
      </div>
    )
  }

  const list = tickets.tickets || []
  if (list.length === 0) {
    return (
      <div className="fd-card">
        <div className="fd-card__label">Meine offenen Tickets</div>
        <div className="fd-card__value fd-card__value--muted">
          Keine offenen Tickets — sauberer Schreibtisch.
        </div>
      </div>
    )
  }

  return (
    <div className="fd-card fd-card--list">
      <div className="fd-card__label">
        Meine offenen Tickets ({list.length})
      </div>
      <ul className="fd-tickets">
        {list.map((t) => (
          <li key={t.id} className="fd-ticket">
            <a
              className="fd-ticket__link"
              href={`https://flawdesk.de/t/${t.nr}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="fd-ticket__nr">#{t.nr}</span>
              <span className="fd-ticket__title">{t.title}</span>
            </a>
            <div className="fd-ticket__meta">
              <span className={`fd-status fd-status--${t.status || 'new'}`}>
                {translateStatus(t.status)}
              </span>
              {t.priority && t.priority !== 'medium' && (
                <span
                  className={`fd-priority fd-priority--${t.priority}`}
                >
                  {t.priority}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function translateStatus(status) {
  const map = {
    new: 'neu',
    'in-progress': 'in Arbeit',
    review: 'Review',
    blocked: 'blockiert',
    fixed: 'erledigt',
    rejected: 'abgelehnt',
  }
  return map[status] || status || 'neu'
}

/**
 * #1088: Zeigt Tickets die mit der aktuell offenen Mail verknuepft sind.
 * Quellen:
 *   - tasksForMail: aus /api/tasks/by-mail, persistent ueber Reloads
 *   - freshTicket: gerade in dieser Session angelegt (createdToast), wird
 *     reingemergt damit der User SOFORT sieht "Ticket angelegt", auch wenn
 *     der Hook noch nicht refetcht hat
 *
 * Match-Anzeige: exact = diese Mail wurde direkt umgewandelt, thread =
 * eine andere Mail im selben Outlook-Thread.
 *
 * Komplett still wenn keine Tickets — keine Karte, kein Hinweis. Wir zeigen
 * nichts wenn nichts da ist, damit die Hauptseite frei bleibt.
 */
function RelatedTasksCard({ tasksForMail, freshTicket }) {
  // Loading: einfache Karte ohne Inhalt — vermeidet Flackern. Im Normalfall
  // ist der Hook nach ~200ms ready, dann sieht der User entweder die Liste
  // oder gar nichts (wenn keine Tickets).
  if (tasksForMail.status === 'loading') {
    return null
  }

  const fromHook = (tasksForMail.tasks || [])
  // Frisch angelegtes Ticket reinmergen — falls noch nicht im Hook drin.
  // Dedup ueber nr+workspace_id (gleiches Ticket koennte aus beiden Quellen
  // kommen wenn der Hook nach createdToast schon einmal refetcht hat).
  let combined = fromHook
  if (freshTicket && freshTicket.nr) {
    const alreadyThere = fromHook.some(
      (t) => t.nr === freshTicket.nr && t.workspace_id === freshTicket.workspace_id,
    )
    if (!alreadyThere) {
      combined = [
        {
          id: freshTicket.id,
          nr: freshTicket.nr,
          title: freshTicket.title,
          status: freshTicket.status || 'new',
          project_id: freshTicket.project_id,
          workspace_id: freshTicket.workspace_id,
          match: 'exact',
          fresh: true, // visuelles Highlight fuer "gerade angelegt"
        },
        ...fromHook,
      ]
    }
  }

  if (combined.length === 0) return null

  return (
    <div className="fd-card fd-card--list">
      <div className="fd-card__label">
        {combined.length === 1
          ? 'Ticket zu dieser Mail'
          : `Tickets zu dieser Mail (${combined.length})`}
      </div>
      <ul className="fd-tickets">
        {combined.map((t) => {
          // #1088: Wenn workspace_slug bekannt ist, kanonische URL mit
          // Workspace-Praefix bauen — FlawDesk wechselt dann automatisch
          // in den richtigen Workspace, statt im aktuellen Scope nach
          // einem #nr zu suchen das es dort nicht gibt. Fallback auf die
          // Legacy-/t/<nr>-Route wenn Slug fehlt (Personal-Tickets oder
          // workspace ohne slug).
          const href = t.workspace_slug
            ? `https://flawdesk.de/w/${t.workspace_slug}/t/${t.nr}`
            : `https://flawdesk.de/t/${t.nr}`
          return (
            <li
              key={`${t.workspace_id || 'personal'}-${t.nr}`}
              className={`fd-ticket${t.fresh ? ' fd-ticket--fresh' : ''}`}
            >
              <a
                className="fd-ticket__link"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="fd-ticket__nr">#{t.nr}</span>
                <span className="fd-ticket__title">{t.title}</span>
              </a>
              <div className="fd-ticket__meta">
                <span className={`fd-status fd-status--${t.status || 'new'}`}>
                  {translateStatus(t.status)}
                </span>
                {t.match === 'thread' && (
                  <span className="fd-match-tag" title="Zu einer anderen Mail im selben Thread">
                    Thread
                  </span>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function translateApiError(err) {
  if (!err) return 'Unbekannter Fehler.'
  const code = err.code || 'unknown'
  const messages = {
    tenant_not_linked:
      'Dieser Microsoft-Tenant ist mit keinem FlawDesk-Workspace verknüpft. Bitte zuerst auf flawdesk.de einen Workspace anlegen und in den Workspace-Einstellungen mit Microsoft verbinden.',
    tenant_not_allowed:
      'Dein Microsoft-Tenant ist noch nicht für FlawDesk freigeschaltet. Bitte den FlawDesk-Admin kontaktieren.',
    tenant_blocked: 'Dein Microsoft-Tenant ist für FlawDesk gesperrt.',
    pending_approval:
      'Dein Account wartet auf Freischaltung durch den Workspace-Owner. Du wirst per Mail benachrichtigt sobald freigeschaltet.',
    user_not_provisioned:
      'Dein Account ist noch nicht in FlawDesk angelegt. Bitte den Admin kontaktieren.',
    account_disabled: 'Dein FlawDesk-Account ist deaktiviert.',
    account_conflict:
      'E-Mail bereits einem anderen FlawDesk-Account zugeordnet.',
    no_email: 'Microsoft hat keine E-Mail-Adresse mitgeschickt. Bitte den Admin kontaktieren.',
    expired: 'Token abgelaufen. Bitte den Tab neu laden.',
    invalid_token: 'Token ungültig — bitte neu anmelden.',
    auth_failed: 'Anmeldung fehlgeschlagen.',
    network_error: 'Verbindung zum FlawDesk-Server nicht möglich.',
  }
  return messages[code] || err.message || 'Anfrage fehlgeschlagen.'
}

// ─────────────────────────────────────────────────────────────────────
// Office.js Helpers
// ─────────────────────────────────────────────────────────────────────

function isInOutlook() {
  return (
    typeof Office !== 'undefined' &&
    Office.context &&
    Office.context.mailbox &&
    Office.context.mailbox.item
  )
}

function readSubject() {
  if (!isInOutlook()) return ''
  try {
    return Office.context.mailbox.item.subject ?? ''
  } catch {
    return ''
  }
}

function readFrom() {
  if (!isInOutlook()) return ''
  try {
    const sender = Office.context.mailbox.item.from
    if (!sender) return ''
    return sender.displayName
      ? `${sender.displayName} <${sender.emailAddress}>`
      : sender.emailAddress
  } catch {
    return ''
  }
}

/**
 * Liest die conversationId der aktuellen Mail.
 *
 * Outlook gruppiert Mails desselben Subjects + References-Header zu einer
 * "Conversation" und vergibt eine eigene ID dafuer. Diese ID brauchen wir,
 * um via Graph alle Mails desselben Threads zu holen (#1088).
 *
 * Property existiert nur in Read-Modus (offene Mail), nicht in Compose-Modus.
 * Aktuell relevant nur fuer "Ticket aus dieser Mail" — das ist Read.
 */
function readConversationId() {
  if (!isInOutlook()) return null
  try {
    return Office.context.mailbox.item.conversationId || null
  } catch {
    return null
  }
}

/**
 * Liest die internetMessageId der aktuellen Mail (RFC-5322 Message-ID).
 *
 * Brauchen wir um beim Submit aus dem Graph-Thread genau die Mail zu finden
 * die der User offen hatte — deren sauberer Plaintext-Body wird dann die
 * Description (#1088).
 */
function readInternetMessageId() {
  if (!isInOutlook()) return null
  try {
    return Office.context.mailbox.item.internetMessageId || null
  } catch {
    return null
  }
}
